<template>
  <div class="dashboard-gestao">
    <section>
      <h2 class="dashboard-secao">Situação e evolução</h2>
      <v-row dense>
        <v-col class="d-flex" cols="12" xl="5" md="4">
          <grafico-dashboard
            titulo="Total de erros por status"
            :carregando="blocos.situacao.carregando"
            :erro="blocos.situacao.erro"
            :opcoes="opcoesStatus"
            :vazio="semItens(situacao, 'total_erros_por_status')"
          />
        </v-col>
        <v-col class="d-flex" cols="12" xl="5" md="4">
          <grafico-dashboard
            titulo="Erros por prioridade"
            :carregando="blocos.situacao.carregando"
            :erro="blocos.situacao.erro"
            :opcoes="opcoesPrioridade"
            :vazio="semItens(situacao, 'erros_por_prioridade')"
          />
        </v-col>
        <v-col v-for="card in cards" :key="card.chave" class="d-flex" cols="12" xl="2" md="4">
          <card-kpi-dashboard
            :carregando="blocos.indicadores.carregando"
            :dados="indicadores && indicadores[card.chave]"
            :destaque="card.destaque"
            :erro="blocos.indicadores.erro"
            :titulo="card.titulo"
          />
        </v-col>
        <v-col class="d-flex" cols="12">
          <grafico-dashboard
            :titulo="tituloNovos"
            :carregando="blocos.situacao.carregando"
            :erro="blocos.situacao.erro"
            :opcoes="opcoesNovos"
            :vazio="semSerie(situacao, 'erros_novos_por_rodada')"
          />
        </v-col>
      </v-row>
    </section>

    <section>
      <h2 class="dashboard-secao">Responsáveis e prazos</h2>
      <v-row dense>
        <v-col class="d-flex" cols="12">
          <grafico-dashboard
            titulo="Erros por responsável"
            :carregando="blocos.responsaveis.carregando"
            :erro="blocos.responsaveis.erro"
            :opcoes="opcoesResponsavel"
            :vazio="semItens(responsaveis, 'erros_por_responsavel')"
          />
        </v-col>
        <v-col cols="12">
          <v-row dense>
            <v-col v-for="tempo in tempos" :key="tempo.chave" class="d-flex" cols="4">
              <v-card class="dashboard-tempo-card flex-grow-1 ma-0" outlined>
                <div class="dashboard-tempo-card__titulo">{{ tempo.titulo }}</div>

                <div
                  v-if="blocos.responsaveis.carregando"
                  class="align-center d-flex dashboard-tempo-card__estado justify-center"
                >
                  <v-progress-circular color="primary" indeterminate size="24" />
                </div>

                <div v-else-if="blocos.responsaveis.erro" class="dashboard-tempo-card__estado dashboard-tempo-card__estado--erro">
                  Não foi possível carregar este indicador.
                </div>

                <div v-else-if="tempoIndisponivel(tempo.chave)" class="dashboard-tempo-card__estado">
                  {{ motivoTempo(tempo.chave) }}
                </div>

                <div v-else-if="tempoSemAmostra(tempo.chave)" class="dashboard-tempo-card__estado">
                  Sem dados para o filtro selecionado
                </div>

                <div
                  v-else-if="textoTempo(tempo.chave)"
                  class="dashboard-tempo"
                  :class="{ 'dashboard-tempo--alerta': tempoAcimaDaMeta(tempo) }"
                >
                  <strong>{{ textoTempo(tempo.chave).valor }}</strong>
                  dias
                  <template v-if="textoTempo(tempo.chave).meta !== null && textoTempo(tempo.chave).meta !== undefined">
                    · meta {{ textoTempo(tempo.chave).meta }}
                  </template>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </section>

    <section>
      <h2 class="dashboard-secao">Onde os erros se concentram</h2>
      <v-row dense>
        <v-col class="d-flex" cols="12">
          <grafico-dashboard
            titulo="Erros por entidade"
            :carregando="blocos.concentracao.carregando"
            :erro="blocos.concentracao.erro"
            :opcoes="opcoesEntidade"
            :vazio="semItens(concentracao, 'erros_por_entidade')"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col class="d-flex" cols="12" xl="7">
          <grafico-dashboard
            titulo="Erros por campo"
            :carregando="blocos.concentracao.carregando"
            :erro="blocos.concentracao.erro"
            :opcoes="opcoesCampo"
            :vazio="semItens(concentracao, 'erros_por_campo')"
          />
        </v-col>
        <v-col class="d-flex" cols="12" xl="5">
          <grafico-dashboard
            titulo="Erros por tipo de validação"
            :carregando="blocos.concentracao.carregando"
            :erro="blocos.concentracao.erro"
            :opcoes="opcoesTipo"
            :vazio="semItens(concentracao, 'erros_por_tipo_validacao')"
          />
        </v-col>
        <v-col class="d-flex" cols="12" xl="7">
          <grafico-dashboard
            titulo="Erros recorrentes"
            :carregando="blocos.recorrencia.carregando"
            :erro="blocos.recorrencia.erro"
            :opcoes="opcoesRecorrentes"
            :vazio="semSerie(recorrencia, 'erros_recorrentes')"
          >
            <template v-if="intervaloRecorrentes" #meta>
              <span class="dashboard-intervalo">{{ intervaloRecorrentes }}</span>
            </template>
          </grafico-dashboard>
        </v-col>
        <v-col class="d-flex" cols="12" xl="5">
          <grafico-dashboard
            titulo="Erros por origem"
            :carregando="blocos.recorrencia.carregando"
            :erro="blocos.recorrencia.erro"
            :opcoes="opcoesOrigem"
            :vazio="semItens(recorrencia, 'erros_por_origem')"
          />
        </v-col>
      </v-row>
    </section>
  </div>
</template>

<script>
  import GestaoBdgdsErrosDashboardService from '@/services/GestaoBdgdsErrosDashboardService.js';
  import CardKpiDashboard from './CardKpiDashboard.vue';
  import GraficoDashboard from './GraficoDashboard.vue';
  import {
    opcoesAreaRecorrentes,
    opcoesBarraOrigem,
    opcoesBarrasHorizontais,
    opcoesBarrasResponsavel,
    opcoesColunasPrioridade,
    opcoesColunasRodada,
    opcoesPareto,
    opcoesRosca,
    opcoesTreemap
  } from './opcoesGraficosDashboard';

  const blocoInicial = () => ({ carregando: false, erro: false });

  export default {
    name: 'DashboardGestaoErros',

    components: {
      CardKpiDashboard,
      GraficoDashboard
    },

    props: {
      filtros: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        indicadores: null,
        responsaveis: null,
        concentracao: null,
        recorrencia: null,
        situacao: null,
        requisicao: 0,
        blocos: {
          indicadores: blocoInicial(),
          responsaveis: blocoInicial(),
          concentracao: blocoInicial(),
          recorrencia: blocoInicial(),
          situacao: blocoInicial()
        },
        cards: [{ chave: 'tarefas_pendentes', titulo: 'Tarefas pendentes', destaque: true }],
        tempos: [
          { chave: 'tempo_medio_em_correcao', titulo: 'Tempo médio de correção' },
          { chave: 'tempo_medio_de_justificativa', titulo: 'Tempo médio de justificativa' },
          { chave: 'tempo_medio_ate_encerramento', titulo: 'Tempo médio de encerramento' }
        ]
      };
    },

    computed: {
      opcoesResponsavel() {
        return this.responsaveis ? opcoesBarrasResponsavel(this.responsaveis.erros_por_responsavel) : {};
      },
      opcoesEntidade() {
        return this.concentracao ? opcoesTreemap(this.concentracao.erros_por_entidade) : {};
      },
      opcoesCampo() {
        return this.concentracao ? opcoesPareto(this.concentracao.erros_por_campo) : {};
      },
      opcoesTipo() {
        return this.concentracao ? opcoesBarrasHorizontais(this.concentracao.erros_por_tipo_validacao) : {};
      },
      intervaloRecorrentes() {
        const itens = this.recorrencia && this.recorrencia.erros_recorrentes && this.recorrencia.erros_recorrentes.itens;
        if (!itens || itens.length < 2) return '';
        return `${itens[0].rotulo} – ${itens[itens.length - 1].rotulo}`;
      },
      opcoesRecorrentes() {
        return this.recorrencia ? opcoesAreaRecorrentes(this.recorrencia.erros_recorrentes) : {};
      },
      opcoesOrigem() {
        return this.recorrencia ? opcoesBarraOrigem(this.recorrencia.erros_por_origem) : {};
      },
      opcoesStatus() {
        return this.situacao ? opcoesRosca(this.situacao.total_erros_por_status) : {};
      },
      opcoesPrioridade() {
        return this.situacao ? opcoesColunasPrioridade(this.situacao.erros_por_prioridade) : {};
      },
      opcoesNovos() {
        return this.situacao ? opcoesColunasRodada(this.situacao.erros_novos_por_rodada) : {};
      },
      tituloNovos() {
        const grafico = this.situacao && this.situacao.erros_novos_por_rodada;
        const quantidade = grafico && grafico.itens ? grafico.itens.length : 0;

        return quantidade ? `Erros novos por rodada (${quantidade})` : 'Erros novos por rodada';
      }
    },

    mounted() {
      this.carregar();
    },

    methods: {
      carregar() {
        const filtros = { ...this.filtros };
        const requisicao = ++this.requisicao;

        this.buscarBloco('indicadores', requisicao, () => GestaoBdgdsErrosDashboardService.indicadoresDashboard(filtros));
        this.buscarBloco('responsaveis', requisicao, () => GestaoBdgdsErrosDashboardService.responsaveisPrazosDashboard(filtros));
        this.buscarBloco('concentracao', requisicao, () => GestaoBdgdsErrosDashboardService.concentracaoDashboard(filtros));
        this.buscarBloco('recorrencia', requisicao, () => GestaoBdgdsErrosDashboardService.recorrenciaOrigemDashboard(filtros));
        this.buscarBloco('situacao', requisicao, () => GestaoBdgdsErrosDashboardService.situacaoEvolucaoDashboard(filtros));
      },

      buscarBloco(nome, requisicao, consulta) {
        this.blocos[nome] = { carregando: true, erro: false };

        consulta()
          .then(({ data }) => {
            if (requisicao !== this.requisicao) return;
            this[nome] = data;
            this.blocos[nome] = { carregando: false, erro: false };
          })
          .catch(() => {
            if (requisicao !== this.requisicao) return;
            this[nome] = null;
            this.blocos[nome] = { carregando: false, erro: true };
          });
      },

      semItens(bloco, chave) {
        return this.graficoVazio(bloco, chave, true);
      },

      semSerie(bloco, chave) {
        return this.graficoVazio(bloco, chave, false);
      },

      graficoVazio(bloco, chave, exigirTotal) {
        if (!bloco || !bloco[chave]) return false;
        const grafico = bloco[chave];
        if (grafico.disponivel === false) return false;
        if (!grafico.itens || grafico.itens.length === 0) return true;
        return exigirTotal && grafico.total === 0;
      },

      tempoIndisponivel(chave) {
        const tempo = this.responsaveis && this.responsaveis[chave];
        return Boolean(tempo && tempo.disponivel === false);
      },

      motivoTempo(chave) {
        const tempo = this.responsaveis && this.responsaveis[chave];
        return (tempo && tempo.motivo) || '';
      },

      tempoSemAmostra(chave) {
        const tempo = this.responsaveis && this.responsaveis[chave];
        return Boolean(tempo && tempo.disponivel && (tempo.valor_dias === null || tempo.amostra === 0));
      },

      textoTempo(chave) {
        const tempo = this.responsaveis && this.responsaveis[chave];
        if (!tempo || !tempo.disponivel || tempo.valor_dias === null || tempo.valor_dias === undefined) return null;
        return {
          valor: new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(tempo.valor_dias),
          meta: tempo.meta
        };
      },

      tempoAcimaDaMeta(tempo) {
        const dados = this.responsaveis && this.responsaveis[tempo.chave];
        return Boolean(tempo.alertaAcimaDaMeta && dados && dados.meta !== null && dados.valor_dias > dados.meta);
      }
    },

    watch: {
      filtros: {
        deep: true,
        handler() {
          this.carregar();
        }
      }
    }
  };
</script>

<style scoped>
  .dashboard-gestao {
    display: flex;
    flex-direction: column;
    gap: 36px;
  }

  .dashboard-secao {
    color: #4f5a67;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    line-height: 1.2;
    margin: 0 0 14px;
    text-transform: uppercase;
  }

  .dashboard-tempo-card {
    border-color: #e2e6eb !important;
    box-shadow: none !important;
    padding: 20px !important;
  }

  .dashboard-tempo-card__titulo {
    color: #17202a;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: 12px;
  }

  .dashboard-tempo-card__estado {
    color: #4f5a67;
    font-size: 13px;
    min-height: 32px;
  }

  .dashboard-tempo-card__estado--erro {
    color: #d14343;
  }

  .dashboard-tempo {
    color: #4f5a67;
    font-size: 13px;
  }

  .dashboard-tempo strong {
    color: #17202a;
    font-size: 20px;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  .dashboard-tempo--alerta,
  .dashboard-tempo--alerta strong {
    color: #b4501a;
    font-weight: 500;
  }

  .dashboard-tempo--alerta strong {
    font-weight: 600;
  }

  .dashboard-intervalo {
    color: #4f5a67;
    font-size: 12px;
  }
</style>
